import React, { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://static.signwell.com/assets/embedded.js";

function loadSignWellScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("SignWell is only available in the browser."));
  }
  if (window.SignWellEmbed) return Promise.resolve();
  const existing = document.querySelector("script[data-signwell-embed]");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Could not load SignWell.")),
        { once: true },
      );
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.dataset.signwellEmbed = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load SignWell."));
    document.body.appendChild(script);
  });
}

export default function SignWellEmbedFrame({
  url,
  containerId,
  onCompleted,
  onClosed,
  onDeclined,
  onError,
}) {
  const embedRef = useRef(null);
  const handlersRef = useRef({});
  handlersRef.current = { onCompleted, onClosed, onDeclined, onError };

  useEffect(() => {
    if (!url || !containerId) return undefined;
    let cancelled = false;

    loadSignWellScript()
      .then(() => {
        if (cancelled || !window.SignWellEmbed) return;
        const embed = new window.SignWellEmbed({
          url,
          containerId,
          allowClose: true,
          showHeader: true,
          events: {
            completed: (event) => handlersRef.current.onCompleted?.(event),
            closed: (event) => handlersRef.current.onClosed?.(event),
            declined: (event) => handlersRef.current.onDeclined?.(event),
            error: (event) => handlersRef.current.onError?.(event),
          },
        });
        embedRef.current = embed;
        embed.open();
      })
      .catch((error) => {
        if (!cancelled) handlersRef.current.onError?.(error);
      });

    return () => {
      cancelled = true;
      try {
        embedRef.current?.close?.();
      } catch (_) {
        /* iframe already gone */
      }
      embedRef.current = null;
    };
  }, [url, containerId]);

  return (
    <div
      id={containerId}
      className="signwell-embed-frame"
      style={{ width: "100%", minHeight: 640, height: "70vh" }}
    />
  );
}
